<script setup lang="ts">
import { computed, defineProps } from "vue";
import type { PoolListItem } from "@/models/pool-list-item.model";
import { formatProbeValue, getProbeType } from "@/shared/utils/probes";
import { isNotNil } from "@/models/nullable.type";
import PoolIcon from "@/features/pools/PoolIcon.vue";

const summaryProbes = [
  { idx: 1, label: "CT1" },
  { idxField: "EauCapteur", label: "CT2" },
  { idxField: "pHCapteur", label: "CT3" },
  { idxField: "TraitCapteur", label: "CT4" },
  { idxField: "PressionCapteur", label: "CT6" },
] as { idx?: number; idxField?: keyof PoolListItem; label: string }[];

const props = defineProps<{
  pool: PoolListItem;
}>();

const values = computed(() => {
  return summaryProbes
    .map(({ idx, idxField, label }) => {
      const probeIdx = (idxField ? props.pool[idxField] : idx) as number;
      const probe = props.pool.probes?.find(
        (probe) => probe.index === probeIdx
      );
      return probe
        ? {
            label,
            value: formatProbeValue(probe.type, probe.filteredValue),
            unit: getProbeType(probe.type).unit,
            unavailable: probe.filteredValue === -1000,
          }
        : undefined;
    })
    .filter(isNotNil);
});
</script>

<template>
  <div class="flex items-center">
    <div class="flex-grow-0 flex-shrink-0" style="width: 32px; height: 32px">
      <pool-icon :pool="pool" />
    </div>
    <div class="ml-3">
      <h1 class="text-md font-medium mb-0.5 flex">
        {{ pool.poolNickname }}
        <div
          v-if="pool.alertCount > 0"
          class="bg-alert h-[7px] w-[7px] ml-1 rounded-3xl"
        />
        <div
          v-else-if="pool.notifyCount > 0"
          class="bg-warning h-[7px] w-[7px] ml-1 rounded-3xl"
        />
      </h1>
      <div class="text-xs">
        <span
          v-for="value in values"
          :key="value.label"
          class="mr-3 text-gray-200"
        >
          {{
            `${$t(value.label)}&nbsp;:&nbsp;${
              value.unavailable ? $t("NotAvail") : `${value.value}${value.unit}`
            }
          `
          }}
        </span>
      </div>
    </div>
  </div>
</template>
